// src/components/DeviceManagement/DeviceList.js
import React from 'react';
import PropTypes from 'prop-types';
import DeviceCard from './DeviceCard';
import './DeviceList.css';

/**
 * Device list component with selector and refresh button
 * 
 * Displays a dropdown selector for devices with refresh capability
 * Shows appropriate message when no devices are available
 * 
 * @param {Object} props - Component props
 * @param {Array} props.devices - Array of device objects
 * @param {string} props.selectedDeviceId - Currently selected device ID
 * @param {Function} props.onDeviceSelect - Callback when device is selected
 * @param {Function} props.onRefresh - Callback to refresh device list
 * @param {boolean} props.isRefreshing - Whether refresh is in progress
 * @param {Function} props.onAddDevice - Callback to open device claiming modal
 * @param {string} props.viewMode - Current view mode ('simplified' or 'detailed')
 */
const DeviceList = ({
  devices,
  selectedDeviceId,
  onDeviceSelect,
  onRefresh,
  isRefreshing,
  onAddDevice,
  viewMode,
}) => {
  // No devices available
  if (devices.length === 0) {
    return (
      <div className="device-list-container no-devices">
        <div className="no-devices-message">
          <p>No devices found for this configuration.</p>
          <button
            className="refresh-button"
            onClick={onRefresh}
            disabled={isRefreshing}
            title="Refresh All Data"
          >
            {isRefreshing ? (
              <>
                <span className="refresh-spinner">↻</span> Refreshing...
              </>
            ) : (
              <>↻ Try Refreshing</>
            )}
          </button>
          <p style={{ marginTop: '12px' }}>
            or{' '}
            <span
              onClick={onAddDevice}
              style={{
                color: 'var(--accent-color)',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              click here
            </span>{' '}
            to add a device to this installation
          </p>
        </div>
      </div>
    );
  }

  // Device selector with dropdown
  return (
    <div className={`device-list-container ${viewMode === 'simplified' ? 'simplified-view' : ''}`}>
      <label htmlFor="device-select">Select a Device:</label>
      <select
        id="device-select"
        value={selectedDeviceId}
        onChange={(e) => onDeviceSelect(e.target.value)}
        disabled={devices.length === 0}
        className="device-selector"
      >
        {devices.map((device) => (
          <DeviceCard
            key={device.deviceId}
            device={device}
            isSelected={device.deviceId === selectedDeviceId}
          />
        ))}
      </select>
      <button
        className="refresh-button"
        onClick={onRefresh}
        disabled={isRefreshing}
        title="Refresh All Data"
      >
        {isRefreshing ? <span className="refresh-spinner">↻</span> : '↻'}
      </button>
    </div>
  );
};

DeviceList.propTypes = {
  devices: PropTypes.arrayOf(
    PropTypes.shape({
      deviceId: PropTypes.string.isRequired,
      displayName: PropTypes.string,
      serialNumber: PropTypes.string,
    })
  ).isRequired,
  selectedDeviceId: PropTypes.string,
  onDeviceSelect: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  isRefreshing: PropTypes.bool.isRequired,
  onAddDevice: PropTypes.func.isRequired,
  viewMode: PropTypes.oneOf(['simplified', 'detailed']).isRequired,
};

DeviceList.defaultProps = {
  selectedDeviceId: '',
};

export default DeviceList;
